import {
  hasPermissionToInitiateTransactions,
  hasPermissionToPostListings,
  isUserAuthorized,
} from './userHelpers';
import {
  NO_ACCESS_PAGE_POST_LISTINGS,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
} from './urlHelpers';

export const SELLER_VERIFICATION_APPROVED_STATUS = 'approved';
export const DEFAULT_LISTING_CREATION_PATH = '/l/new';
export const SELLER_VERIFICATION_PENDING_STATUS = 'pending';
export const SELLER_VERIFICATION_CHANGES_REQUESTED_STATUS = 'changes_requested';
export const SELLER_VERIFICATION_REJECTED_STATUS = 'rejected';
export const SELLER_VERIFICATION_NOTIFIABLE_STATUSES = new Set([
  SELLER_VERIFICATION_CHANGES_REQUESTED_STATUS,
  SELLER_VERIFICATION_APPROVED_STATUS,
  SELLER_VERIFICATION_REJECTED_STATUS,
]);

const normalizeString = value => (typeof value === 'string' ? value.trim() : '');
const normalizeStatus = value => normalizeString(value).toLowerCase();

const userPrivateData = currentUser =>
  currentUser?.attributes?.profile?.privateData ||
  currentUser?.attributes?.privateData ||
  currentUser?.privateData ||
  {};

const normalizeFallback = fallback => {
  const value = normalizeString(fallback);
  return value ? value : null;
};

export const getSellerVerificationStatus = currentUser => {
  const status = userPrivateData(currentUser)?.sellerVerification?.status;
  return normalizeStatus(status);
};

export const isSellerApproved = currentUser =>
  getSellerVerificationStatus(currentUser) === SELLER_VERIFICATION_APPROVED_STATUS;

export const hasMarketplaceListingAccess = currentUser =>
  isUserAuthorized(currentUser) && hasPermissionToPostListings(currentUser);

export const hasMarketplacePurchaseAccess = currentUser =>
  isUserAuthorized(currentUser) && hasPermissionToInitiateTransactions(currentUser);

export const canSellerCreateNewListing = currentUser =>
  isSellerApproved(currentUser) && hasMarketplaceListingAccess(currentUser);

export const canSellerPublishListing = currentUser => canSellerCreateNewListing(currentUser);

export const isSellerVerificationPendingReview = currentUser =>
  getSellerVerificationStatus(currentUser) === SELLER_VERIFICATION_PENDING_STATUS;

export const canSubmitSellerVerification = ({
  currentUser,
  isAuthenticated = Boolean(currentUser?.id),
  isProvider = false,
} = {}) => {
  const status = getSellerVerificationStatus(currentUser);
  const canAccessSellerVerification = Boolean(isAuthenticated && isProvider);

  return (
    canAccessSellerVerification &&
    status !== SELLER_VERIFICATION_APPROVED_STATUS &&
    status !== SELLER_VERIFICATION_PENDING_STATUS
  );
};

/**
 * Seller approval on Chevalier Casks is intentionally split into separate checks:
 * 1. Sharetribe account state (`currentUser.attributes.state`) controls whether the marketplace
 *    account is active for authenticated access checks used here (`canBrowse`).
 * 2. Custom seller verification (`profile.privateData.sellerVerification.status`) controls
 *    whether Chevalier's seller review is complete.
 * 3. Sharetribe permission set `initiateTransactions` controls buying access.
 * 4. Sharetribe permission set `postListings` controls listing creation/submission access.
 *
 * Approval matrix used by seller-facing UI and routing:
 * - `canBrowse`: marketplace account is active.
 * - `canBuy`: marketplace account is active and transaction permission is allowed.
 * - `canSubmitSellerVerification`: seller can submit or resubmit seller verification now.
 * - `canCreateListing`: seller verification is approved and posting permission is active.
 * - `canPublishListing`: same as `canCreateListing`; the seller can submit the listing into
 *   the listing workflow, but live publishing is still moderated separately.
 */
export const getSellerApprovalMatrix = ({
  currentUser,
  isAuthenticated = Boolean(currentUser?.id),
  isProvider = false,
} = {}) => {
  const sellerVerificationStatus = getSellerVerificationStatus(currentUser);
  const marketplaceAccountActive = isUserAuthorized(currentUser);
  const hasListingPermission = hasPermissionToPostListings(currentUser);
  const hasPurchasePermission = hasPermissionToInitiateTransactions(currentUser);
  const sellerVerificationApproved =
    sellerVerificationStatus === SELLER_VERIFICATION_APPROVED_STATUS;
  const sellerVerificationPendingReview =
    sellerVerificationStatus === SELLER_VERIFICATION_PENDING_STATUS;
  const canBrowse = marketplaceAccountActive;
  const canBuy = canBrowse && hasPurchasePermission;
  const canCreateListing = sellerVerificationApproved && canBrowse && hasListingPermission;
  const canPublishListing = canCreateListing;
  const sellerVerificationNavigationVisible = Boolean(
    currentUser?.id && isAuthenticated && isProvider && !sellerVerificationApproved
  );

  return {
    sellerVerificationStatus,
    marketplaceAccountActive,
    hasListingPermission,
    hasPurchasePermission,
    canBrowse,
    canBuy,
    canSubmitSellerVerification: canSubmitSellerVerification({
      currentUser,
      isAuthenticated,
      isProvider,
    }),
    canCreateListing,
    canPublishListing,
    sellerVerificationApproved,
    sellerVerificationPendingReview,
    marketplaceApprovalPending: Boolean(currentUser?.id) && !canBrowse,
    listingAccessPending:
      sellerVerificationApproved && marketplaceAccountActive && !hasListingPermission,
    purchaseAccessPending: marketplaceAccountActive && !hasPurchasePermission,
    sellerVerificationNavigationVisible,
  };
};

export const shouldShowSellerVerificationNavigation = ({
  isAuthenticated,
  isProvider,
  currentUser,
}) =>
  getSellerApprovalMatrix({
    currentUser,
    isAuthenticated,
    isProvider,
  }).sellerVerificationNavigationVisible;

export const getSellerVerificationNotificationEventId = currentUser => {
  const status = getSellerVerificationStatus(currentUser);
  if (!SELLER_VERIFICATION_NOTIFIABLE_STATUSES.has(status)) {
    return null;
  }

  const sellerVerification = userPrivateData(currentUser)?.sellerVerification || {};
  const explicitEventId = normalizeString(sellerVerification?.notificationEmail?.eventId);
  if (explicitEventId) {
    return explicitEventId;
  }

  const reviewedAt = normalizeString(sellerVerification?.reviewedAt);
  return reviewedAt ? `seller-verification:${status}:${reviewedAt}` : null;
};

export const buildPathFromLocation = location => {
  if (!location || typeof location !== 'object') {
    return '';
  }

  const pathname = normalizeString(location.pathname);
  const search = normalizeString(location.search);
  const hash = normalizeString(location.hash);

  return `${pathname}${search}${hash}`;
};

export const sanitizeNextPath = (nextPath, fallback = DEFAULT_LISTING_CREATION_PATH) => {
  const candidate = normalizeString(nextPath);
  const fallbackPath = normalizeFallback(fallback);

  if (!candidate) {
    return fallbackPath;
  }

  const startsWithSlash = candidate.startsWith('/');
  const startsWithDoubleSlash = candidate.startsWith('//');
  if (!startsWithSlash || startsWithDoubleSlash) {
    return fallbackPath;
  }

  return candidate;
};

export const createSellerVerificationNextSearch = nextPath => {
  const safeNext = sanitizeNextPath(nextPath, DEFAULT_LISTING_CREATION_PATH);
  return safeNext ? `?next=${encodeURIComponent(safeNext)}` : '';
};

export const parseNextPathFromSearch = search => {
  const rawSearch = normalizeString(search);
  const params = new URLSearchParams(rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch);
  return sanitizeNextPath(params.get('next'), null);
};

// Seller verification is a marketplace-specific review state.
// Sharetribe permission sets still remain the source of truth for whether the user
// can actually post listings or initiate purchases.
export const getCreateListingRedirectTarget = (
  currentUser,
  nextPath = DEFAULT_LISTING_CREATION_PATH
) => {
  if (!currentUser?.id) {
    return { name: 'NewListingPage' };
  }

  const approvalMatrix = getSellerApprovalMatrix({
    currentUser,
    isAuthenticated: true,
    isProvider: true,
  });

  if (!approvalMatrix.sellerVerificationApproved) {
    return {
      name: 'SellerVerificationPage',
      to: {
        search: createSellerVerificationNextSearch(nextPath),
      },
    };
  }

  if (approvalMatrix.marketplaceApprovalPending) {
    return {
      name: 'NoAccessPage',
      params: {
        missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
      },
    };
  }

  if (!approvalMatrix.canCreateListing) {
    return {
      name: 'NoAccessPage',
      params: {
        missingAccessRight: NO_ACCESS_PAGE_POST_LISTINGS,
      },
    };
  }

  return {
    name: 'NewListingPage',
  };
};

export const getApprovedSellerVerificationRedirectTarget = currentUser => {
  if (!currentUser?.id || !isSellerApproved(currentUser)) {
    return null;
  }

  return canSellerCreateNewListing(currentUser)
    ? { name: 'ManageListingsPage' }
    : { name: 'AccountSettingsPage' };
};
